import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Trash2, UploadCloud } from "lucide-react";

export default function LibraryPage() {
  const [photos, setPhotos] = useState([]);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [drag, setDrag] = useState(false);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/library/photos").then((r) => setPhotos(r.data));
  useEffect(() => { load(); }, []);

  const uploadFiles = async (files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (arr.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: arr.length });
    let ok = 0;
    for (const file of arr) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/library/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
        setProgress({ done: ok, total: arr.length });
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || "error"}`);
      }
    }
    setUploading(false);
    setProgress({ done: 0, total: 0 });
    toast.success(`${ok}/${arr.length} imagen(es) subidas`);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    uploadFiles(e.dataTransfer.files);
  };

  const remove = async (id) => {
    await api.delete(`/library/photos/${id}`);
    load();
  };

  const filtered = filter
    ? photos.filter((p) => (p.label || "").toLowerCase().includes(filter.toLowerCase()))
    : photos;

  return (
    <div className="stagger">
      <PageHeader
        overline="Biblioteca visual"
        title="Banco de imágenes."
        subtitle="Sube tu archivo de fotos de viaje para reutilizarlas en landings, anuncios y emails. Arrastra varias fotos a la vez. Máx 5MB por imagen."
        action={
          <>
            <input data-testid="library-upload-input" ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => uploadFiles(e.target.files)} className="hidden" />
            <Button data-testid="library-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">
              <Upload className="w-4 h-4 mr-2" />{uploading ? `Subiendo ${progress.done}/${progress.total}…` : "Subir fotos"}
            </Button>
          </>
        }
      />

      {/* Drag & drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`mb-6 border-2 border-dashed rounded-sm py-10 text-center transition-colors cursor-pointer ${drag ? "border-[#D95D39] bg-[#D95D39]/5" : "border-[#E2DCD0] bg-white"}`}
        onClick={() => fileRef.current?.click()}
        data-testid="library-dropzone"
      >
        <UploadCloud className="w-8 h-8 mx-auto text-[#8A8F98]" strokeWidth={1.5} />
        <p className="mt-3 text-sm text-[#5C616A]">Arrastra tus fotos aquí o haz clic para seleccionarlas</p>
        <p className="text-[11px] text-[#8A8F98] mt-1">PNG, JPG, WEBP · Máx 5MB cada una · Selección múltiple permitida</p>
      </div>

      <div className="flex items-center justify-between mb-5">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98]">
          {photos.length} foto(s) en tu biblioteca
        </p>
        <Input
          placeholder="Filtrar..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs rounded-sm bg-white border-[#E2DCD0]"
        />
      </div>

      {photos.length === 0 ? (
        <Card className="edit-card p-12 rounded-sm shadow-none text-center text-[#5C616A]">
          Aún no hay fotos en tu biblioteca.
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <Card key={p.id} className="edit-card rounded-sm shadow-none overflow-hidden group relative">
              <img src={p.url} alt={p.label} className="w-full h-44 object-cover" />
              <div className="p-3">
                <p className="text-xs text-[#1A1F2B] truncate">{p.label}</p>
                <p className="text-[10px] text-[#8A8F98] font-mono">{(p.size / 1024).toFixed(1)} KB</p>
              </div>
              <button
                onClick={() => remove(p.id)}
                className="absolute top-2 right-2 w-7 h-7 rounded-sm bg-white/90 border border-[#E2DCD0] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`library-delete-${p.id}`}
              >
                <Trash2 className="w-3 h-3 text-[#9D0208]" />
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
